import type { ReactNode } from 'react';
import { useNavigation, useOne, useTranslate } from '@refinedev/core';
import { Popover, Space, Typography, Tooltip, Spin, Image } from 'antd';
import {
  EditOutlined,
  LinkOutlined,
  ReadOutlined,
  CustomerServiceOutlined,
  ShopOutlined,
  SoundOutlined,
} from '@ant-design/icons';
import type { ModStatus, PublicationListOut } from '@/api/generated/schemas';
import { ModStatusTag } from '@/components/common/ModStatusTag';
import { StatusDot } from '@/components/common/StatusDot';
import { statusTone } from '@/components/common/statusTone';
import { PublicationDrawerIcon } from './drawer/PublicationDrawerIcon';

const { Text, Link } = Typography;

const RESOURCE = 'user_catalog.publications';

interface PublicationInfoPopoverProps {
  /** Full publication record. When provided, no fetch is performed. */
  publication?: PublicationListOut;
  /** Fallback id used to fetch the publication when `publication` is absent. */
  publicationId?: number;
  /** Overrides the trigger text (defaults to the publication title). */
  children?: ReactNode;
}

export const PublicationInfoPopover = ({
  publication,
  publicationId,
  children,
}: PublicationInfoPopoverProps) => {
  const t = useTranslate();
  const { show } = useNavigation();

  const shouldFetch = !publication && publicationId != null;
  const { result, query } = useOne<PublicationListOut>({
    resource: RESOURCE,
    id: publicationId,
    queryOptions: { enabled: shouldFetch },
  });

  const pub = publication ?? result;
  const id = pub?.id ?? publicationId;
  const triggerText = children ?? pub?.title ?? id;

  const publisherName = pub?.publisher?.name;
  const personNames = (
    items: { position: number; name?: string | null }[] | undefined
  ) =>
    (items ?? [])
      .slice()
      .sort((a, b) => a.position - b.position)
      .map((item) => item.name)
      .filter(Boolean)
      .join(', ');
  const authorNames = personNames(
    pub?.publication_authors?.map((a) => ({
      position: a.position,
      name: a.author?.name,
    }))
  );
  const lectorNames = personNames(
    pub?.publication_lectors?.map((l) => ({
      position: l.position,
      name: l.lector?.name,
    }))
  );

  const hasEbook = pub?.ebook != null || pub?.ebook_source_id != null;
  const hasAudio = pub?.audio != null || pub?.audio_source_id != null;

  // Each format carries its own moderation status, separate from the
  // publication's. It is missing when the format is known only by its source
  // id, and then no dot is drawn — an amber one would read as "test".
  const formats: {
    kind: 'ebook' | 'audio';
    icon: ReactNode;
    modStatus?: ModStatus | null;
  }[] = [];
  if (hasEbook) {
    formats.push({
      kind: 'ebook',
      icon: <ReadOutlined />,
      modStatus: pub?.ebook?.mod_status,
    });
  }
  if (hasAudio) {
    formats.push({
      kind: 'audio',
      icon: <CustomerServiceOutlined />,
      modStatus: pub?.audio?.mod_status,
    });
  }

  const content =
    shouldFetch && query.isLoading && !pub ? (
      <Spin size="small" />
    ) : (
      <Space align="start" size={12}>
        {(pub?.cover_url || hasEbook || hasAudio) && (
          <Space direction="vertical" size={4} align="center">
            {pub?.cover_url && (
              <Image
                src={pub.cover_url}
                width={72}
                style={{ objectFit: 'cover' }}
              />
            )}
            {formats.length > 0 && (
              <Space size={8}>
                {formats.map(({ kind, icon, modStatus }) => (
                  <Space key={kind} size={2}>
                    <Tooltip
                      title={t(`user_catalog.publications.tabs.${kind}`)}
                    >
                      {icon}
                    </Tooltip>
                    {modStatus && (
                      <StatusDot
                        tone={statusTone(modStatus)}
                        title={`${t(`user_catalog.publications.tabs.${kind}`)}: ${t(
                          `base.mod_status.${modStatus}`
                        )}`}
                      />
                    )}
                  </Space>
                ))}
              </Space>
            )}
          </Space>
        )}
        <Space direction="vertical" size={4}>
          <Text type="secondary">{`${t('base.fields.id')}: ${pub?.id ?? id ?? '-'}`}</Text>
          {publisherName && (
            <Text type="secondary">
              <ShopOutlined style={{ marginRight: 4 }} />
              {publisherName}
            </Text>
          )}
          {authorNames && (
            <Text type="secondary">
              <EditOutlined style={{ marginRight: 4 }} />
              {authorNames}
            </Text>
          )}
          {lectorNames && (
            <Text type="secondary">
              <SoundOutlined style={{ marginRight: 4 }} />
              {lectorNames}
            </Text>
          )}
          <ModStatusTag value={pub?.mod_status} />
        </Space>
      </Space>
    );

  return (
    <Space size={4}>
      <Popover
        trigger="hover"
        title={pub?.title}
        content={content}
        styles={{ root: { maxWidth: 320 } }}
      >
        {triggerText}
      </Popover>
      {id != null && <PublicationDrawerIcon publicationId={id} />}
      <Link
        aria-label={t('actions.show')}
        onClick={(e) => {
          e.stopPropagation();
          if (id != null) show(RESOURCE, id);
        }}
      >
        <LinkOutlined />
      </Link>
    </Space>
  );
};
